import { useEffect } from 'react';
import { GLOBAL_FORMAT_DEFAULTS, SUPPORTED_PORTAL_LOCALES } from '@heglo/ui/tokens';
import { apiClient } from '@/api/client';
import { useAuthStore, type TenantPresentationMeta } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';

interface TenantMetaResponse extends TenantPresentationMeta {
  enabledLocales?: string[];
  defaultLocale?: string;
}

export function useTenantMeta() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const role = useAuthStore((state) => state.role);
  const tenantId = useAuthStore((state) => state.tenantId);
  const impersonatedTenantId = useAuthStore((state) => state.impersonatedTenantId);
  const tenantMetaLoaded = useAuthStore((state) => state.tenantMetaLoaded);
  const setTenantMeta = useAuthStore((state) => state.setTenantMeta);
  const activeTenantId = role === 'superadmin' ? impersonatedTenantId : tenantId;

  useEffect(() => {
    if (!isAuthenticated || activeTenantId == null || tenantMetaLoaded) return;

    apiClient.get<TenantMetaResponse>('/tenants/meta').then(({ data }) => {
      setTenantMeta(data);
      const enabled = (data.enabledLocales || []).filter(
        (locale): locale is 'ar' | 'en' => SUPPORTED_PORTAL_LOCALES.includes(locale as 'ar' | 'en'),
      );
      useUIStore.getState().setTenantLanguages(
        enabled.length > 0 ? enabled : [...SUPPORTED_PORTAL_LOCALES],
        data.defaultLocale === 'en' ? 'en' : 'ar',
      );
    }).catch(() => setTenantMeta({}));
  }, [activeTenantId, isAuthenticated, setTenantMeta, tenantMetaLoaded]);
}

export function getTenantCurrency(): string {
  return useAuthStore.getState().tenantCurrency ?? GLOBAL_FORMAT_DEFAULTS.currency;
}

export function getTenantTimezone(): string {
  return useAuthStore.getState().tenantTimezone ?? GLOBAL_FORMAT_DEFAULTS.timezone;
}

export function getTenantTimeFormat(): '12h' | '24h' {
  return useAuthStore.getState().tenantTimeFormat ?? '12h';
}

export function getTenantPhonePrefix(): string {
  return useAuthStore.getState().tenantPhonePrefix ?? GLOBAL_FORMAT_DEFAULTS.phonePrefix;
}
